import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { LogoIcon, contact1, contact2, contact3, s4, s5, s6, s7, s8 } from '../assets';
import { FiArrowRight } from 'react-icons/fi';
import { motion } from "framer-motion";
import { Reveal } from "./reveal";
import styles from '../style';
import { Link } from 'react-router-dom';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, SplitText);

const Slider = () => {
  const sliderRef = useRef(null);
  const progressBarRef = useRef(null);
  const sliderImagesRef = useRef(null);
  const sliderTitleRef = useRef(null);
  const sliderSubtitleRef = useRef(null);
  const sliderIndicesRef = useRef(null);
  const activeSlideRef = useRef(0);
  const currentSplitTitleRef = useRef(null);
  const currentSplitSubtitleRef = useRef(null);
  const lenisRef = useRef(null);
  const scrollTriggerRef = useRef(null);

  const slides = [
    {
      title: "Clearer reporting pathways",
      subtitle: "Staff understand how to record, report and escalate concerns appropriately.",
      image: s8,
    },
    {
      title: "More confident staff",
      subtitle: "Training is practical, scenario based and rooted in real school life.",
      image: s4,
    },
    {
      title: "Stronger safeguarding culture",
      subtitle: "Safeguarding becomes more visible in routines, conversations and adult practice.",
      image: s7,
    },
    {
      title: "Improved leadership clarity",
      subtitle: "Leaders are supported to identify priorities, address gaps and plan next steps.",
      image: s5,
    },
    {
      title: "Practical tools & recommendations",
      subtitle: "Schools receive focused summaries, roadmaps or tools depending on the selected engagement.",
      image: s6,
    },
  ];

  const createIndices = () => {
    if (!sliderIndicesRef.current) return;
    
    sliderIndicesRef.current.innerHTML = "";
    
    slides.forEach((_, index) => {
      const indexNum = (index + 1).toString().padStart(2, "0");
      const indicatorElement = document.createElement("p");
      indicatorElement.dataset.index = index;
      indicatorElement.className = "flex items-center gap-2 cursor-pointer font-mono text-sm text-third hover:opacity-100 transition-opacity duration-300";
      indicatorElement.innerHTML = `
        <span class="marker w-5 h-0.5 bg-black origin-left inline-block"></span>
        <span class="index">${indexNum}</span>
      `;
      sliderIndicesRef.current.appendChild(indicatorElement);
    });
    
    // Set initial states for all indicators
    const indicators = sliderIndicesRef.current.querySelectorAll("p");
    indicators.forEach((indicator, i) => {
      const markerEl = indicator.querySelector(".marker");
      const indexEl = indicator.querySelector(".index");
      if (markerEl && indexEl) {
        if (i === 0) {
          gsap.set(markerEl, { scaleX: 1 });
          gsap.set(indexEl, { opacity: 1 });
        } else {
          gsap.set(markerEl, { scaleX: 0 });
          gsap.set(indexEl, { opacity: 0.35 });
        }
      }
    });
  };

  const animateNewTitle = (index) => {
    if (!sliderTitleRef.current) return;
    
    if (currentSplitTitleRef.current) {
      currentSplitTitleRef.current.revert();
    }

    // Clear and add new title
    sliderTitleRef.current.innerHTML = '';
    const titleElement = document.createElement('h1');
    // Fixed typography classes - properly sized and visible
    titleElement.className = "text-primary text-md font-bold text-start max-w-[600px] px-6 leading-tight sm:leading-tight md:leading-tight";
    titleElement.textContent = slides[index].title;
    sliderTitleRef.current.appendChild(titleElement);

    currentSplitTitleRef.current = new SplitText(titleElement, {
      type: "lines",
      linesClass: "line overflow-hidden block",
    });

    gsap.set(currentSplitTitleRef.current.lines, {
      yPercent: 100,
      opacity: 0,
    });

    gsap.to(currentSplitTitleRef.current.lines, {
      yPercent: 0,
      opacity: 1,
      duration: 0.75,
      stagger: 0.1,
      ease: "power2.out",
    });
  };

  const animateNewSubtitle = (index) => {
    if (!sliderSubtitleRef.current) return;
    
    if (currentSplitSubtitleRef.current) {
      currentSplitSubtitleRef.current.revert();
    }

    // Clear and add new subtitle
    sliderSubtitleRef.current.innerHTML = '';
    const subtitleElement = document.createElement('p');
    // Fixed typography classes - properly sized and visible
    subtitleElement.className = "text-primary md:text-sm text-mm text-start max-w-[600px] leading-tight px-4 mt-4 sm:mt-6 font-light";
    subtitleElement.textContent = slides[index].subtitle;
    sliderSubtitleRef.current.appendChild(subtitleElement);

    currentSplitSubtitleRef.current = new SplitText(subtitleElement, {
      type: "lines",
      linesClass: "line overflow-hidden block",
    });

    gsap.set(currentSplitSubtitleRef.current.lines, {
      yPercent: 100,
      opacity: 0,
    });

    gsap.to(currentSplitSubtitleRef.current.lines, {
      yPercent: 0,
      opacity: 1,
      duration: 0.75,
      stagger: 0.1,
      delay: 0.2,
      ease: "power2.out",
    });
  };

  const animateNewIndicators = (index) => {
    if (!sliderIndicesRef.current) return;
    
    const indicators = sliderIndicesRef.current.querySelectorAll("p");

    indicators.forEach((indicator, i) => {
      const markerElement = indicator.querySelector(".marker");
      const indexElement = indicator.querySelector(".index");
      
      if (!markerElement || !indexElement) return;

      if (i === index) {
        gsap.to(indexElement, {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(markerElement, {
          scaleX: 1,
          duration: 0.3,
          ease: "power2.out",
        });
      } else {
        gsap.to(indexElement, {
          opacity: 0.35,
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(markerElement, {
          scaleX: 0,
          duration: 0.3,
          ease: "power2.out",
        });
      }
    });
  };

  const animateNewSlide = (index) => {
    if (!sliderImagesRef.current) return;
    
    const newSliderImage = document.createElement("img");
    newSliderImage.src = slides[index].image;
    newSliderImage.alt = `${index + 1}`;
    // Fixed image classes - ensures w-full and h-screen
    newSliderImage.className = "absolute top-0 left-0 w-full h-screen object-cover";
    newSliderImage.style.width = "100%";
    newSliderImage.style.height = "100vh";
    newSliderImage.style.objectFit = "cover";
    newSliderImage.loading = "lazy";

    gsap.set(newSliderImage, {
      opacity: 0,
      scale: 1,
    });

    // Clear existing images and add new one
    while (sliderImagesRef.current.firstChild) {
      sliderImagesRef.current.removeChild(sliderImagesRef.current.firstChild);
    }
    
    sliderImagesRef.current.appendChild(newSliderImage);

    gsap.to(newSliderImage, {
      opacity: 1,
      duration: 0.8,
      ease: "power2.inOut",
    });

    animateNewTitle(index);
    animateNewSubtitle(index);
    animateNewIndicators(index);
  };

  useEffect(() => {
    if (!sliderRef.current) return;

    // Initialize Lenis for smooth scrolling
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    lenis.on("scroll", ScrollTrigger.update);
    
    gsap.ticker.add((time) => {
      lenis.raf(time * 1000);
    });
    gsap.ticker.lagSmoothing(0);

    const pinDistance = window.innerHeight * slides.length ;
    
    createIndices();
    
    // Initial slide setup
    setTimeout(() => {
      animateNewSlide(0);
    }, 100);

    // Create ScrollTrigger
    scrollTriggerRef.current = ScrollTrigger.create({
      trigger: sliderRef.current,
      start: "top top",
      end: `+=${pinDistance}px`,
      scrub: 1,
      pin: true,
      pinSpacing: true,
      onUpdate: (self) => {
        if (progressBarRef.current) {
          gsap.set(progressBarRef.current, {
            scaleY: self.progress,
          });
        }

        const currentSlide = Math.floor(self.progress * slides.length);
        
        if (activeSlideRef.current !== currentSlide && currentSlide < slides.length) {
          activeSlideRef.current = currentSlide;
          animateNewSlide(activeSlideRef.current);
        }
      },
    });

    // Cleanup
    return () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
      }
      if (lenisRef.current) {
        lenisRef.current.destroy();
      }
      if (currentSplitTitleRef.current) {
        currentSplitTitleRef.current.revert();
      }
      if (currentSplitSubtitleRef.current) {
        currentSplitSubtitleRef.current.revert();
      }
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      gsap.killTweensOf(progressBarRef.current);
    };
  }, []);

  return (
    <div
      className="relative w-full min-h-screen overflow-hidden bg-white" ref={sliderRef}
    >
      {/* Progress Bar */}
      <div 
        className="fixed right-14 top-24 w-1 h-3/4 bg-gradient-to-t from-third to-white origin-top z-20 scale-y-0 rounded-full"
        ref={progressBarRef}
      ></div>
      
      {/* Images Container - Fixed for full screen */}
      <div className="relative w-full h-screen overflow-hidden" ref={sliderImagesRef}>
        {/* Images will be added dynamically */}
      </div>
      
      {/* Title and Subtitle Container - Fixed positioning and visibility */}
      <div className={`absolute bottom-60 z-20 w-full flex flex-col md:flex-row items-start justify-center gap-20 pb-28 px-10 pointer-events-none`}>
        <h2 className="pl-8 md:text-mm text-md font-semibold text-primary w-[100px]">
          Our Impact
        </h2>
        <div className="md:pl-8 flex flex-col items-start gap-6">
          {/* Title */}
          <div ref={sliderTitleRef} className="w-full"></div>
          
          {/* Subtitle */}
          <div ref={sliderSubtitleRef} className="pl-2 w-full"></div>

          <Link to="/Expertise">
            <Reveal>
              <motion.div 
                initial="initial"
                whileHover="hovered"
                className="pl-6 md:pl-8 py-8 flex flex-row items-center justify-center gap-4 cursor-pointer group relative z-10 overflow-hidden"
              >
              {/* Arrow icon container with animation */}
              <div className="w-[40px] h-[40px] relative overflow-hidden">
                <motion.div 
                  variants={{
                    initial: { y: 0 },
                    hovered: { y: "-100%" },
                  }}
                  className="absolute inset-0 bg-second flex items-center justify-center"
                >
                  <FiArrowRight size={20} className="text-fourth" />
                </motion.div>
                <motion.div 
                  variants={{
                    initial: { y: "100%" },
                    hovered: { y: 0 },
                  }}
                  className="absolute inset-0 bg-fourth flex items-center justify-center"
                >
                  <FiArrowRight size={20} className="text-second" />
                </motion.div>
              </div>
              
              {/* Text with animation */}
              <div className="relative overflow-hidden">
                <motion.h2 
                  variants={{
                    initial: { y: 0 },
                    hovered: { y: "-100%" },
                  }}
                  className="text-primary xs:text-sm text-mm font-semibold font-semibold"
                >
                  View All Stats
                </motion.h2>
                <motion.h2 
                  variants={{
                    initial: { y: "100%" },
                    hovered: { y: 0 },
                  }}
                  className="absolute inset-0 text-second xs:text-sm text-mm font-semibold font-semibold"
                >
                  View All Stats
                </motion.h2>
              </div>
            </motion.div>
            </Reveal>
          </Link>
        </div>
      </div>
      
      {/* Indices */}
      <div className="fixed right-6 bottom-20 mb-20 flex flex-col gap-3 z-[100]" ref={sliderIndicesRef}>
        {/* Indices will be added dynamically */}
      </div>

      
      {/* Top gradient overlay */}
      <div className="absolute inset-0 bg-black/50 via-transparent pointer-events-none z-[5]"></div>
    </div>
  );
};

export default Slider;